import React, { useEffect, useState } from 'react';
import { CalendarClock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import client from '../../api/client';

let TZ;
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: TZ || undefined }) : '—');

const RunRow = ({ r }) => (
  <li data-testid="auto-decline-run" className="py-2 text-xs flex flex-wrap justify-between gap-2">
    <span className="text-[#2A2A2A]">{when(r.ran_at)}{r.backfill ? ' · earlier run' : ''}</span>
    <span className="text-[#6A6A6A]">
      {r.declined} declined · {r.sms_sent} texted · {r.email_sent} emailed
      {r.failed ? <span className="text-red-700"> · {r.failed} failed</span> : null}
      {r.queued > (r.dispatched || 0) ? <span className="text-[#A15A2A]"> · waiting for send window</span> : null}
      {r.summary_status === 'sent' ? ' · summary texted to you' : r.summary_status === 'pending' ? ' · your summary text is queued' : null}
    </span>
  </li>
);

// Live preview of the next auto-decline run + its message timing + history.
export const AutoDeclineStatus = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => client.get('/auto-decline/status').then(({ data }) => setS(data)).catch(() => {});
    load();
    window.addEventListener('rsvp-config:saved', load);
    return () => window.removeEventListener('rsvp-config:saved', load);
  }, []);
  if (!s || !s.enabled) return null;
  TZ = s.timezone;

  return (
    <div data-testid="auto-decline-status" className="mt-4 rounded-xl bg-[#FBF7F2] border border-[#EFE3D8] p-4 space-y-3">
      <div className="flex items-start gap-2.5">
        <CalendarClock className="w-4 h-4 text-[#7B2C3B] mt-0.5 flex-shrink-0" />
        <div className="text-sm text-[#2A2A2A]" data-testid="auto-decline-next-run">
          {s.next_run_at ? (
            <>
              <strong>Next run: {when(s.next_run_at)}.</strong> {s.pending_guests} guest{s.pending_guests === 1 ? '' : 's'} will be declined
              {s.messaging_on && (s.will_text || s.will_email_only)
                ? <>, then messaged at <strong>{when(s.messages_at)}</strong> ({s.will_text} by text, {s.will_email_only} by email only).</>
                : '.'}
            </>
          ) : s.already_ran ? (
            <>Auto-decline already ran for your reply-by date ({s.reply_by}).{s.queued_messages ? ` ${s.queued_messages} message${s.queued_messages === 1 ? '' : 's'} waiting for your send window (${s.send_window[0]}–${s.send_window[1]}).` : ''}</>
          ) : 'Not scheduled yet.'}
          <p className="text-[11px] text-[#9A8A7E] mt-0.5">Messages only go out inside your send window ({s.send_window[0]}–{s.send_window[1]}, set under Reminders).</p>
        </div>
      </div>
      {s.warnings.length > 0 ? (
        <ul data-testid="auto-decline-warnings" className="space-y-1">
          {s.warnings.map((w) => (
            <li key={w} className="text-xs text-[#7A3E1A] flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {w}</li>
          ))}
        </ul>
      ) : (
        <p data-testid="auto-decline-all-clear" className="text-xs text-[#2F5D2F] flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Everything is set for messages to go out.</p>
      )}
      {s.runs.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E]">Run history</p>
          <ul data-testid="auto-decline-runs" className="divide-y divide-[#EFE3D8]">{s.runs.map((r) => <RunRow key={r.id} r={r} />)}</ul>
        </div>
      )}
    </div>
  );
};

export default AutoDeclineStatus;
